<script setup lang="ts">
import AvatarFull from '@/assets/icons/Avatars/AvatarFull.vue';
import AvatarLite from '@/assets/icons/Avatars/AvatarLite.vue';
import AvatarMedium from '@/assets/icons/Avatars/AvatarMedium.vue';
import { useInAppPurchase } from '@/composables/useInAppPurchase';
import { usePlatform } from '@/composables/usePlatform';
import type { SubscriptionPlan } from '@/types/api';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const { isNative, isIOS } = usePlatform();
const { isInitialized: isIAPInitialized, getApplePrice } = useInAppPurchase();

const props = defineProps<{
  plan: SubscriptionPlan;
  selected?: boolean;
  showFeatures?: boolean;
  isYearly?: boolean;
  hidePrice?: boolean;
}>();

const emit = defineEmits<{
  (e: 'select', plan: SubscriptionPlan): void;
}>();

const getAvatarComponent = (plan: SubscriptionPlan) => {
  const avatarType = plan.avatar ?? 'lite';
  if (avatarType === 'medium') return AvatarMedium;
  if (avatarType === 'full') return AvatarFull;
  return AvatarLite;
};

// Check if we should use Apple IAP prices
const useApplePricing = computed(() => {
  return isNative.value && isIOS.value && isIAPInitialized.value;
});

// Get the currency to display
const displayCurrency = computed(() => {
  if (useApplePricing.value) {
    const billingPeriod = props.isYearly ? 'yearly' : 'monthly';
    const applePrice = getApplePrice(props.plan, billingPeriod);
    return applePrice.currency || 'CHF';
  }
  return 'CHF';
});

const displayPrice = computed(() => {
  const billingPeriod = props.isYearly ? 'yearly' : 'monthly';

  // On native iOS with IAP initialized, use Apple prices directly
  if (useApplePricing.value) {
    const applePrice = getApplePrice(props.plan, billingPeriod);
    if (applePrice.priceValue) return applePrice.priceValue;
  }

  const monthlyPrice = Number(props.plan.price_monthly) || 0;
  const yearlyPrice = Number(props.plan.price_yearly) || 0;

  if (props.isYearly && yearlyPrice > 0) return yearlyPrice.toFixed(2);
  if (!props.isYearly && monthlyPrice > 0) return monthlyPrice.toFixed(2);
  if (yearlyPrice > 0) return yearlyPrice.toFixed(2);
  if (monthlyPrice > 0) return monthlyPrice.toFixed(2);
  return '0.00';
});

const priceLabel = computed(() => {
  const monthlyPrice = Number(props.plan.price_monthly) || 0;
  const yearlyPrice = Number(props.plan.price_yearly) || 0;

  if (monthlyPrice === 0 && yearlyPrice === 0) return '';

  if (useApplePricing.value) {
    return props.isYearly ? t('subscription.yearly') : t('subscription.monthly');
  }

  if (props.isYearly && yearlyPrice > 0) return t('subscription.yearly');
  if (!props.isYearly && monthlyPrice > 0) return t('subscription.monthly');
  if (yearlyPrice > 0) return t('subscription.yearly');
  return t('subscription.monthly');
});

// const priceSubLabel = computed(() => '');

const featuresList = [
  { key: 'ai_generations_per_month', type: 'number' },
  { key: 'employees_included', type: 'number' },
  { key: 'admin_accounts', type: 'number' },
  { key: 'module_orders', type: 'bool' },
  { key: 'module_staff', type: 'bool' },
  // { key: 'custom_branding', type: 'bool' },
  { key: 'social_sharing', type: 'bool' },
];
</script>

<template>
  <div
    class="flex w-full min-w-40 max-w-96 cursor-pointer flex-col items-center rounded-2xl border-2 bg-gradient-to-br from-white to-primary-100 px-0 py-2 shadow-sm sm:w-1/2"
    :class="[selected ? 'border-accent' : 'border-primary-900']"
    @click="emit('select', plan)"
  >
    <div class="mb-2 font-bold text-lg uppercase tracking-wider">
      {{ plan.name.toUpperCase() }}
    </div>
    <component :is="getAvatarComponent(plan)" class="mb-2 h-28 w-16" />
    <div v-if="!hidePrice">
      <div class="mb-0.5 font-bold text-xl text-primary-900">
        <div>{{ displayCurrency }} {{ displayPrice }}</div>
      </div>
      <div class="mb-3 flex flex-col items-center text-xs text-primary-900">
        <p>{{ priceLabel }}</p>
      </div>
    </div>
    <ul v-if="showFeatures" class="mb-2 w-full px-4 text-left text-xs">
      <li v-for="feature in featuresList" :key="feature.key" class="flex items-center justify-between gap-2 py-0.5">
        <span class="font-semibold">{{ t(`subscription.features.${feature.key}.label`) }}</span>
        <div v-if="feature.type === 'bool'">
          <i v-if="plan.features[feature.key]" class="pi pi-check text-green-600"></i>
          <i v-else class="pi pi-times text-gray-400"></i>
        </div>
        <div v-else-if="feature.type === 'number'">
          <span v-if="feature.key === 'ai_generations_per_month' && plan.features[feature.key] >= 999">
            {{ t('subscription.unlimited') }}
          </span>
          <span v-else-if="feature.key === 'employees_included' && plan.features[feature.key] >= 999">
            {{ t('subscription.unlimited') }}
          </span>
          <span v-else>{{ plan.features[feature.key] }}</span>
        </div>
        <div v-else-if="feature.type === 'string'">
          <span>{{ plan.features[feature.key] }}</span>
        </div>
      </li>
    </ul>
  </div>
</template>
